/**
 * Passo 3 do portão — os seis dígitos.
 *
 * Validade, espera entre reenvios, teto de reenvios, tentativas e bloqueio
 * são da API; esta tela conta o tempo a partir dos prazos que ela devolve
 * (`maquinaDeIdentidade.ts`) e mostra a frase de cada resultado.
 */

import { CheckCircle2, Clock, Info, KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { useId } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/ui/base/input-otp';
import { Label } from '@/ui/base/label';

import { AvisoDoPasso } from './AvisoDoPasso';
import { ChipDeResumo } from './ChipDeResumo';
import {
  DIGITOS_DO_CODIGO,
  codigoExpirou,
  destinoDoCodigo,
  emMinutosESegundos,
  estaBloqueado,
  podeReenviarCodigo,
  reenviosRestantes,
  rotuloDoReenvio,
  segundosDeBloqueio,
  segundosDoCodigo,
  tentativasRestantes,
  type PropsDoPassoDaIdentidade,
} from './maquinaDeIdentidade';

export function IdentidadeCodigo({ estado, despachar, acoes }: PropsDoPassoDaIdentidade) {
  const idDoCampo = useId();
  const codigo = estado.fluxo?.codigo ?? null;
  const bloqueado = estaBloqueado(estado);
  const expirado = codigoExpirou(estado);
  const restamReenvios = reenviosRestantes(estado);
  const rotuloDoCanal = codigo?.canal === 'SMS' ? 'Celular' : 'E-mail';
  const destino = destinoDoCodigo(estado);

  return (
    <div className="space-y-4">
      {codigo && destino && (
        <div className="flex flex-wrap gap-1.5">
          <ChipDeResumo
            rotulo={rotuloDoCanal}
            valor={destino}
            aoAlterar={() => despachar({ tipo: 'ir_para', passo: 'canal' })}
          />
        </div>
      )}

      <div className="space-y-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <KeyRound aria-hidden="true" className="h-5 w-5 text-primary" />
          Confirme o código
        </h3>
        <p className="text-sm text-muted-foreground">
          Enviamos um código de {DIGITOS_DO_CODIGO} dígitos{' '}
          {destino ? (
            <>
              para <strong className="wrap-break-word font-mono">{destino}</strong>
            </>
          ) : (
            'para o contato escolhido'
          )}
          .{' '}
          {expirado ? (
            <span className="font-medium text-destructive">O código venceu — peça um novo.</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-foreground">
              <Clock aria-hidden="true" className="h-3.5 w-3.5" /> vale por{' '}
              <span className="font-mono font-medium">
                {emMinutosESegundos(segundosDoCodigo(estado))}
              </span>
            </span>
          )}
        </p>
      </div>

      {bloqueado && (
        <Alert variant="destructive">
          <ShieldAlert aria-hidden="true" className="h-4 w-4" />
          <AlertTitle>Conferência bloqueada</AlertTitle>
          <AlertDescription>
            Muitas tentativas erradas. Tente de novo em{' '}
            <span className="font-mono font-semibold">
              {emMinutosESegundos(segundosDeBloqueio(estado))}
            </span>
            , pedindo um novo código.
          </AlertDescription>
        </Alert>
      )}

      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (estado.codigo.length === DIGITOS_DO_CODIGO && !bloqueado && !estado.ocupado) {
            void acoes.conferirCodigo();
          }
        }}
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label htmlFor={idDoCampo}>Código de verificação</Label>
            {codigo && !bloqueado && (
              <span className="text-xs text-muted-foreground">
                {tentativasRestantes(estado)} tentativa
                {tentativasRestantes(estado) === 1 ? '' : 's'} antes do bloqueio
              </span>
            )}
          </div>
          <InputOTP
            id={idDoCampo}
            maxLength={DIGITOS_DO_CODIGO}
            value={estado.codigo}
            onChange={(valor) => despachar({ tipo: 'digitar_codigo', codigo: valor })}
            disabled={bloqueado}
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={`Código de ${DIGITOS_DO_CODIGO} dígitos`}
          >
            <InputOTPGroup>
              {Array.from({ length: DIGITOS_DO_CODIGO }, (_, posicao) => (
                <InputOTPSlot key={posicao} index={posicao} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <AvisoDoPasso aviso={estado.aviso} />

        <Button
          type="submit"
          className="w-full"
          disabled={estado.codigo.length !== DIGITOS_DO_CODIGO || bloqueado || estado.ocupado}
        >
          {estado.ocupado ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : (
            <CheckCircle2 aria-hidden="true" />
          )}
          Confirmar código
        </Button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <button
          type="button"
          onClick={() => despachar({ tipo: 'ir_para', passo: 'canal' })}
          className="rounded-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          Trocar canal
        </button>
        <span className="flex items-center gap-2" aria-live="polite">
          {codigo && restamReenvios > 0 && (
            <span className="text-muted-foreground">
              {restamReenvios} reenvio{restamReenvios > 1 ? 's' : ''} restante
              {restamReenvios > 1 ? 's' : ''}
            </span>
          )}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            disabled={!podeReenviarCodigo(estado)}
            onClick={() => void acoes.enviarCodigo()}
          >
            {rotuloDoReenvio(estado)}
          </Button>
        </span>
      </div>

      <p className="flex items-start gap-1.5 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs leading-snug text-foreground">
        <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          Não chegou? Confira a caixa de spam, espere alguns minutos ou peça outro código. Se você
          não tem mais acesso a esse contato,{' '}
          <button
            type="button"
            onClick={() =>
              despachar({
                tipo: 'orientar',
                motivo: codigo?.canal === 'SMS' ? 'TELEFONE_DESATUALIZADO' : 'EMAIL_DESATUALIZADO',
              })
            }
            className="rounded-sm text-link underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          >
            veja as opções
          </button>
          .
        </span>
      </p>
    </div>
  );
}

export default IdentidadeCodigo;
